import { useRecoilValue, useSetRecoilState } from 'recoil';
import { fetchDownloadDicomImageBySOP } from '~/backend/connector-api/connector-components';
import { StructureSet } from '~/domain/patient/structureset';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import { loadingOverlayState } from '~/state/atoms/viewerAtoms';

export default function useStructureSetSelection({
    onSelect,
}: {
    onSelect?: () => void;
}) {
    const { setEnvironmentStructureSet, initializeEnvironment } =
        useViewportEnvironment();
    const { processImportedFiles } = useProcessDicomFiles();
    const patient = useSelectedPatient();
    const sessionData = useRecoilValue(sessionDataAtom);
    const setLoadingOverlay = useSetRecoilState(loadingOverlayState);

    const handleSelectLoadedStructureSet = async (
        structureSet: StructureSet
    ) => {
        onSelect?.();
        setEnvironmentStructureSet(
            [structureSet],
            patient?.dataSet.PatientID ?? ''
        );
    };

    const handleSelectSessionStructureSet = async (id: string) => {
        const downloadedStructureSet = patient?.structureSets.find(
            (structureSet) => structureSet.dicomUid === id
        );
        if (downloadedStructureSet) {
            handleSelectLoadedStructureSet(downloadedStructureSet);
        } else if (sessionData?.payload?.patient?.id) {
            onSelect?.();
            setLoadingOverlay({
                isOpen: true,
                message: 'Downloading structure set...',
            });
            const structureSetBlob = await fetchDownloadDicomImageBySOP({
                pathParams: {
                    patientID: sessionData.payload.patient.id,
                    modality: 'RTSTRUCT',
                    sopInstanceUID: id,
                },
            });
            const files = [
                new File(
                    [structureSetBlob],
                    `${sessionData.payload.context_structure_set.sop_instance_uid}.dcm`
                ),
            ];
            const processedItems = await processImportedFiles(files);
            setLoadingOverlay({
                isOpen: false,
            });
            if (processedItems) {
                initializeEnvironment(processedItems);
            }
        }
    };

    return { handleSelectLoadedStructureSet, handleSelectSessionStructureSet };
}
